import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Store, User, Mail, Phone, Calendar, DollarSign, Package, ShoppingCart, CreditCard, Receipt } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useSettings } from '../../contexts/SettingsContext';
import { Shop, shopService } from '../../services/shopService';
import { format } from 'date-fns';

interface ViewShopDetailsModalProps {
  isOpen: boolean;
  shop: Shop | null;
  onClose: () => void;
}

const ViewShopDetailsModal: React.FC<ViewShopDetailsModalProps> = ({ isOpen, shop, onClose }) => {
  const { theme } = useTheme();
  const { getSetting } = useSettings();
  const [isClosing, setIsClosing] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Get currency symbol from shop settings, fallback to global setting or $
  const currencySymbol = shop?.currency || getSetting('currency_symbol') || '$';

  // Fetch payment history when shop changes
  useEffect(() => {
    if (shop?.id) {
      setLoadingHistory(true);
      shopService.getPaymentHistory(shop.id)
        .then(data => {
          setPaymentHistory(data);
        })
        .catch(error => {
          console.error('Failed to load payment history:', error);
          setPaymentHistory([]);
        })
        .finally(() => {
          setLoadingHistory(false);
        });
    }
  }, [shop?.id]);

  if (!isOpen || !shop) return null;

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      trial: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      expired: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      suspended: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
    };
    
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${styles[status] || styles.suspended}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getPaymentBadge = (status: string) => {
    const styles: Record<string, string> = {
      paid: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    };
    
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${styles[status] || styles.pending}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 150);
  };

  return createPortal(
    <div 
      className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-50 overflow-y-auto ${
        isClosing ? 'animate-fadeOut' : 'animate-fadeIn'
      }`}
      style={{ touchAction: 'manipulation' }}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div 
        className={`w-full max-w-3xl max-h-[90vh] flex flex-col rounded-xl shadow-2xl ${
          isClosing ? 'animate-scaleOut' : 'animate-scaleIn'
        } ${theme === 'dark' ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-gray-200'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-b rounded-t-xl flex-shrink-0 ${
          theme === 'dark' ? 'border-slate-700 bg-slate-800/95' : 'border-slate-200 bg-gray-50/95'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 min-w-0 flex-1">
              <div className={`p-1.5 rounded-lg flex-shrink-0 ${
                theme === 'dark' ? 'bg-blue-900/50' : 'bg-blue-100'
              }`}>
                <Store className={`w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 ${
                  theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                }`} />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className={`text-sm sm:text-base md:text-lg font-semibold truncate ${
                  theme === 'dark' ? 'text-white' : 'text-slate-900'
                }`}>
                  {shop.shop_name}
                </h2>
                <p className={`text-xs truncate ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Shop ID: #{shop.id}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className={`p-1.5 rounded-lg transition-colors touch-manipulation ${
                theme === 'dark'
                  ? 'hover:bg-slate-700 text-gray-400 hover:text-white'
                  : 'hover:bg-gray-100 text-gray-500 hover:text-gray-900'
              }`}
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 sm:px-5 md:px-6 py-4 sm:py-5 flex-1 overflow-y-auto">
          <div className="space-y-4 sm:space-y-5">
            {/* Status and Payment */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className={`block text-xs sm:text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Subscription Status
                </label>
                {getStatusBadge(shop.subscription_status)}
              </div>
              <div>
                <label className={`block text-xs sm:text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Payment Status
                </label>
                {getPaymentBadge(shop.payment_status)}
              </div>
            </div>

              {/* Owner Information */}
              <div className={`p-3 sm:p-4 rounded-xl ${theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                <h4 className={`text-xs sm:text-sm font-semibold mb-3 flex items-center gap-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  <User className="h-3 w-3 sm:h-4 sm:w-4" />
                  Owner Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      Name
                    </label>
                    <p className={`text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {shop.owner_name || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      Username
                    </label>
                    <p className={`text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {shop.owner?.username || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      Email
                    </label>
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-gray-500" />
                      <p className={`text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {shop.owner_email || 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      Phone
                    </label>
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-gray-500" />
                      <p className={`text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {shop.owner_phone || 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className={`block text-xs font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      Business Type
                    </label>
                    <p className={`text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {shop.business_type || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Subscription Details */}
              <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                <h4 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  <CreditCard className="h-4 w-4" />
                  Subscription Details
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      Created At
                    </label>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-500" />
                      <p className={`text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {format(new Date(shop.created_at), 'MMM dd, yyyy HH:mm')}
                      </p>
                    </div>
                  </div>
                  {shop.subscription_starts_at && (
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        Subscription Started
                      </label>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-500" />
                        <p className={`text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          {format(new Date(shop.subscription_starts_at), 'MMM dd, yyyy')}
                        </p>
                      </div>
                    </div>
                  )}
                  {shop.subscription_status === 'trial' && shop.trial_ends_at && (
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        Trial Ends
                      </label>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-500" />
                        <p className={`text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          {format(new Date(shop.trial_ends_at), 'MMM dd, yyyy HH:mm')}
                        </p>
                      </div>
                    </div>
                  )}
                  {shop.subscription_ends_at && (
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        Subscription Ends
                      </label>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-500" />
                        <p className={`text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          {format(new Date(shop.subscription_ends_at), 'MMM dd, yyyy HH:mm')}
                        </p>
                      </div>
                    </div>
                  )}
                  {shop.days_remaining !== undefined && shop.days_remaining !== null && (
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        Days Remaining
                      </label>
                      <p className={`text-lg font-bold ${
                        shop.days_remaining <= 7 ? 'text-red-600' : 
                        shop.days_remaining <= 30 ? 'text-yellow-600' : 
                        'text-green-600'
                      }`}>
                        {Math.round(shop.days_remaining)} days
                      </p>
                    </div>
                  )}
                  {shop.last_payment_at && (
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        Last Payment
                      </label>
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-gray-500" />
                        <p className={`text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          {format(new Date(shop.last_payment_at), 'MMM dd, yyyy')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Statistics */}
              <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                <h4 className={`text-sm font-semibold mb-3 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Shop Statistics
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className={`inline-flex p-3 rounded-lg mb-2 ${theme === 'dark' ? 'bg-purple-900/30' : 'bg-purple-100'}`}>
                      <Package className={`h-6 w-6 ${theme === 'dark' ? 'text-purple-400' : 'text-purple-600'}`} />
                    </div>
                    <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {shop.stats?.total_products || shop.products_count || 0}
                    </p>
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      Products
                    </p>
                  </div>
                  <div className="text-center">
                    <div className={`inline-flex p-3 rounded-lg mb-2 ${theme === 'dark' ? 'bg-green-900/30' : 'bg-green-100'}`}>
                      <ShoppingCart className={`h-6 w-6 ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`} />
                    </div>
                    <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {shop.stats?.total_sales || shop.sales_count || 0}
                    </p>
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      Sales
                    </p>
                  </div>
                  <div className="text-center">
                    <div className={`inline-flex p-3 rounded-lg mb-2 ${theme === 'dark' ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
                      <DollarSign className={`h-6 w-6 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
                    </div>
                    <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {currencySymbol}{shop.stats?.total_revenue ? shop.stats.total_revenue.toLocaleString() : '0'}
                    </p>
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      Revenue
                    </p>
                  </div>
                </div>
              </div>

              {/* Payment History */}
              <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                <h4 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  <Receipt className="h-4 w-4" />
                  Payment History
                </h4>
                {loadingHistory ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className={`mt-2 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      Loading payment history...
                    </p>
                  </div>
                ) : paymentHistory.length === 0 ? (
                  <div className="text-center py-8">
                    <Receipt className={`h-12 w-12 mx-auto mb-2 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`} />
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      No payment history available
                    </p>
                  </div>
                ) : (
                  <div>
                    {/* Desktop table view - hidden on mobile */}
                    <div className="hidden sm:block overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            <th className="text-left pb-2">Date</th>
                            <th className="text-left pb-2">Amount</th>
                            <th className="text-left pb-2">Method</th>
                            <th className="text-left pb-2">Period</th>
                            <th className="text-left pb-2">Reference</th>
                            <th className="text-left pb-2">Recorded By</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                          {paymentHistory.map((payment) => (
                            <tr key={payment.id} className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                              <td className="py-3">
                                {format(new Date(payment.payment_date), 'MMM dd, yyyy')}
                              </td>
                              <td className="py-3 font-semibold text-green-600 dark:text-green-400">
                                {currencySymbol}{parseFloat(payment.amount).toLocaleString()}
                              </td>
                              <td className="py-3">
                                <span className={`px-2 py-1 rounded text-xs ${
                                  theme === 'dark' ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'
                                }`}>
                                  {payment.payment_method.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                                </span>
                              </td>
                              <td className="py-3 text-xs">
                                {payment.subscription_starts_at && payment.subscription_ends_at ? (
                                  <div>
                                    <div>{format(new Date(payment.subscription_starts_at), 'MMM dd, yyyy')}</div>
                                    <div className={theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}>
                                      to {format(new Date(payment.subscription_ends_at), 'MMM dd, yyyy')}
                                    </div>
                                    <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                      ({payment.subscription_months} {payment.subscription_months === 1 ? 'month' : 'months'})
                                    </div>
                                  </div>
                                ) : (
                                  <span className={theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}>N/A</span>
                                )}
                              </td>
                              <td className="py-3">
                                {payment.reference ? (
                                  <span className="font-mono text-xs">{payment.reference}</span>
                                ) : (
                                  <span className={theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}>-</span>
                                )}
                              </td>
                              <td className="py-3">
                                {payment.recorded_by?.name || 'System'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile card view - visible only on mobile */}
                    <div className="sm:hidden space-y-3">
                      {paymentHistory.map((payment) => (
                        <div key={payment.id} className={`p-3 rounded-lg border ${
                          theme === 'dark' ? 'bg-gray-600/30 border-gray-600' : 'bg-white border-gray-200'
                        }`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                              {format(new Date(payment.payment_date), 'MMM dd, yyyy')}
                            </span>
                            <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                              {currencySymbol}{parseFloat(payment.amount).toLocaleString()}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <span className={`text-gray-500 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Method:</span>
                              <div className="mt-1">
                                <span className={`px-2 py-1 rounded text-xs ${
                                  theme === 'dark' ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'
                                }`}>
                                  {payment.payment_method.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                                </span>
                              </div>
                            </div>
                            
                            <div>
                              <span className={`text-gray-500 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Recorded by:</span>
                              <div className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                {payment.recorded_by?.name || 'System'}
                              </div>
                            </div>
                          </div>

                          {payment.subscription_starts_at && payment.subscription_ends_at && (
                            <div className="mt-2">
                              <span className={`text-gray-500 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Period:</span>
                              <div className={`mt-1 text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                {format(new Date(payment.subscription_starts_at), 'MMM dd, yyyy')} - {format(new Date(payment.subscription_ends_at), 'MMM dd, yyyy')}
                                <span className={`ml-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                  ({payment.subscription_months} {payment.subscription_months === 1 ? 'month' : 'months'})
                                </span>
                              </div>
                            </div>
                          )}

                          {payment.reference && (
                            <div className="mt-2">
                              <span className={`text-gray-500 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Reference:</span>
                              <div className="mt-1 font-mono text-xs text-gray-600 dark:text-gray-300">
                                {payment.reference}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    {paymentHistory.length > 0 && paymentHistory[0].notes && (
                      <div className={`mt-3 p-3 rounded text-xs ${theme === 'dark' ? 'bg-gray-600/50 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                        <strong>Note:</strong> {paymentHistory[0].notes}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-t rounded-b-xl flex-shrink-0 ${
            theme === 'dark' ? 'border-gray-700 bg-slate-800/95' : 'border-gray-200 bg-gray-50/95'
          }`}>
            <div className="flex justify-end">
            <button
              type="button"
              onClick={handleClose}
              className={`px-4 sm:px-5 md:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-medium rounded-xl border transition-all duration-200 shadow-lg hover:shadow-xl touch-manipulation ${
                theme === 'dark'
                  ? 'border-gray-600 text-gray-300 hover:bg-slate-700 hover:border-gray-500'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
              }`}
            >
              Close
            </button>
            </div>
          </div>
      </div>
    </div>,
    document.body
  );
};

export default ViewShopDetailsModal;
